import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Save, Lock, Loader2 } from 'lucide-react';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const READ_ONLY_FIELDS = [
  ['employee_id', 'Employee ID'], ['department', 'Department'], ['designation', 'Designation'],
  ['category', 'Category'], ['date_of_joining', 'Date of Joining'], ['employment_type', 'Employment Type'],
  ['gender', 'Gender'], ['date_of_birth', 'Date of Birth'],
];
const EDITABLE_FIELDS = [
  ['phone', 'Phone'], ['address', 'Address'], ['emergency_contact', 'Emergency Contact'],
  ['blood_group', 'Blood Group'], ['highest_qualification', 'Highest Qualification'],
];

export const MyEmployeeProfile = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const headers = () => {
    const token = localStorage.getItem('hrms-token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/me/employee-profile`, { headers: headers(), credentials: 'include' });
        const d = await safeJson(res);
        if (res.ok && d && d.employee_id) {
          setProfile(d);
          const f = {};
          EDITABLE_FIELDS.forEach(([k]) => { f[k] = d[k] || ''; });
          setForm(f);
        }
      } catch { /* no linked profile */ }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/me/employee-profile`, {
        method: 'PUT', headers: headers(), credentials: 'include', body: JSON.stringify(form)
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to save');
      toast.success('Personal details saved');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!profile) return null;

  return (
    <Card data-testid="my-employee-profile">
      <CardHeader>
        <CardTitle>My Employee Record</CardTitle>
        <CardDescription>
          Personal details below are editable. Fields marked <Lock className="w-3 h-3 inline" /> (Department, Designation, Category…)
          can only be changed via <strong>Request Correction</strong> at the bottom of this page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Official Details (read-only)</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {READ_ONLY_FIELDS.filter(([k]) => profile[k]).map(([k, label]) => (
              <div key={k} className="rounded-md border bg-slate-50 px-2.5 py-1.5" data-testid={`ro-${k}`}>
                <p className="text-[10px] text-slate-400 flex items-center gap-1"><Lock className="w-2.5 h-2.5" />{label}</p>
                <p className="text-sm font-medium truncate">{String(profile[k])}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Personal Details (editable)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EDITABLE_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="btn-primary" data-testid="save-my-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Personal Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MyEmployeeProfile;
